// All-WebGL streaming pipeline — P3 lift, shared GLSL (STREAM_PIPELINE.md).
//
// GLSL ES 3.00 port of the UV→3D Gaussian lift (`wgsl/lift_pass1.wgsl` +
// `wgsl/lift_pass2.wgsl`, cross-checked against the CPU reference `inference/
// lift.ts`). Two pieces, each reused twice:
//   PASS1_FS    — full fragment shader: per UV texel → (pos, cov6, opacity).
//   LIFT_SAMPLE — `bool liftSample(...)`: pass2's per-sample bilinear + eigen-
//                 decomp + cull. In P3c it's dropped verbatim into the spark
//                 generator dyno.
//
// All inputs are addressed as flat R32F "data textures" of a known width `tw`:
// linear index i → texel (i%tw, i/tw). Values that are logically ints (face
// ids, vertex indices, validity) are exact in fp32 at our sizes (< 2^24).

// Linear-index fetch from an R32F data texture.
const F1 = /* glsl */ `
float f1(highp sampler2D t, int i, int tw) {
  return texelFetch(t, ivec2(i % tw, i / tw), 0).r;
}`;

// The trainer's scale map, shared by all three lifters (inference/splatScale.ts).
// logsigmoid is written with exp on its non-overflowing side in each branch, the
// same reason torch uses F.logsigmoid and the TS reference branches.
const SPLAT_SCALE = /* glsl */ `
float logSigmoid(float x) {
  return x >= 0.0 ? -log(1.0 + exp(-x)) : x - log(1.0 + exp(x));
}
float splatScale(float raw) {
  float t = raw + uScaleOffset;
  float ls = (uBounded != 0.0) ? uLogSigmaMax + logSigmoid(t) : t;
  return exp(min(ls, uScaleLogMax));
}`;

// ---------------------------------------------------------------------------
// PASS 1 — per UV texel p: J·offset + base position, quat→R, cov = M·Mᵀ.
// Mirrors lift_pass1.wgsl / lift.ts:138-219. Output across 3 RGBA32F targets:
//   o0 = (pos.x, pos.y, pos.z, c00)
//   o1 = (c01, c02, c11, c12)
//   o2 = (c22, opacity_logit, 0, 0)
// ---------------------------------------------------------------------------
export const PASS1_FS = /* glsl */ `#version 300 es
precision highp float;
precision highp int;
uniform highp sampler2D geomTex;       // 11*HW, channel-major (c*HW + p)
uniform highp sampler2D vertsTex;      // V*3
#if GPUJAC
uniform highp sampler2D facesTex;      // F*3 vertex indices (static; jac computed inline)
uniform int twFaces;
#else
uniform highp sampler2D jacTex;        // F*9
uniform highp sampler2D faceValidTex;  // F
uniform int twJac, twFaceValid;
#endif
uniform highp sampler2D idximTex;      // HW*3
uniform highp sampler2D barimTex;      // HW*3
uniform highp sampler2D triimTex;      // HW
uniform highp sampler2D validTex;      // HW
uniform int twGeom, twVerts, twIdxim, twBarim, twTriim, twValid;
uniform int nVerts;   // vertex count, for torch's negative-index wrap
uniform int H, W, HW, outW;
// ── Triangle-bounded splat clamp: MUST mirror training exactly ──────────────
// uTriKappa   > 0 : a splat's world extent is projected down so it cannot
//                   exceed uTriKappa * triangle_bound(edge, area) of its host
//                   face. 0 = off.
// uSliverQMin > 0 : faces below this shape quality host NO splat, and the bound
//                   above derates linearly over [q, 5q]. 0 = off.
//
// This is a port of scene/multi_part_gaussian_model.py's lift-time projection
// and utils/geom_reg.py's triangle_bound/triangle_quality. It has to exist on
// BOTH sides: the clamp is applied AFTER the decoder, so the ONNX graph does
// not carry it, and a web render without it paints splats the trained model
// never had. Same class of bug as the face-validity threshold documented above
// -- the browser inventing geometry PyTorch never produced.
uniform float uTriKappa;
uniform float uSliverQMin;
// The trainer's scale map (inference/splatScale.ts), which this shader used to
// skip entirely -- it exponentiated the RAW decoder log-scale, applying neither
// the bias nor the ceiling. uBounded selects the map: 1 = bounded sigma
// (log(sigmaMax) + logsigmoid(raw + offset)), 0 = additive (raw + offset).
// Defaults 0/0/0/+inf reproduce the old unbiased, unclamped behaviour.
uniform float uBounded;
uniform float uLogSigmaMax;
uniform float uScaleOffset;
uniform float uScaleLogMax;
layout(location = 0) out vec4 o0;
layout(location = 1) out vec4 o1;
layout(location = 2) out vec4 o2;
${F1}
${SPLAT_SCALE}
// NO BACKTICKS IN THIS BLOCK. It sits inside the PASS1_FS template literal, so
// a backtick here ENDS the shader string and the module stops parsing -- which
// is what happened: the production build failed outright and the WebGL lift
// path was dead at import, not at draw. The two names below were quoted with
// backticks and deliberately are not any more.
//
// -1 IS A REAL INDEX IN TORCH. idxim holds it at every texel outside a UV
// chart, and the trainer's verts2[idxim] WRAPS it to the last vertex the way
// numpy does. Reading texel -3 of a data texture does not: it clamps or wraps
// on the texture, giving a base position from somewhere else entirely. Same
// defect as lift.ts and lift_pass1.wgsl carried, in a third spelling -- which
// is exactly why the three lifters have to be changed together.
vec3 vert(int vi) {
  int o = (vi < 0 ? vi + nVerts : vi) * 3;
  return vec3(f1(vertsTex, o, twVerts), f1(vertsTex, o + 1, twVerts), f1(vertsTex, o + 2, twVerts));
}
void main() {
  int p = int(gl_FragCoord.y) * outW + int(gl_FragCoord.x);
  if (p >= HW) { o0 = vec4(0.0); o1 = vec4(0.0); o2 = vec4(0.0); return; }

  int fid = int(f1(triimTex, p, twTriim));

#if GPUJAC
  // Per-face Jacobian computed INLINE from verts (no per-frame jac upload):
  // columns [v1-v0, v2-v0, safe_sqrt_normalized(n)]. Mirrors lift.ts:67-95.
  vec3 e0 = vec3(0.0), e1 = vec3(0.0), e2 = vec3(0.0); float fv = 0.0;
  if (fid >= 0) {
    int i0f = int(f1(facesTex, fid * 3 + 0, twFaces));
    int i1f = int(f1(facesTex, fid * 3 + 1, twFaces));
    int i2f = int(f1(facesTex, fid * 3 + 2, twFaces));
    vec3 v0 = vert(i0f);
    e0 = vert(i1f) - v0; e1 = vert(i2f) - v0;
    vec3 nrm = cross(e0, e1);
    float nlen = length(nrm);
    // No area threshold — training has none (safe_sqrt_normalize clamps |n|^2
    // to 1e-20 and always keeps the real edge basis). "> 0.0" is false for NaN,
    // which is the case the identity-Jacobian fallback below guards. Gating on
    // area instead sends sliver triangles to identity, which applies the
    // decoder's UV-space offsets in raw world units and yields a ~0.4
    // world-unit opaque splat behind the head. See wgsl/jacobian.wgsl.
    fv = (nlen > 0.0) ? 1.0 : 0.0;
    e2 = nrm * ((nlen > 0.0) ? 1.0 / sqrt(max(nlen, 1e-10)) : 0.0);   // r2 = n * sqrt(1/|n|)
  }
#else
  float fv = (fid >= 0) ? f1(faceValidTex, fid, twFaceValid) : 0.0;
#endif
  bool isValid = (f1(validTex, p, twValid) != 0.0) && (fid >= 0) && (fv != 0.0);

  // Identity Jacobian on invalid texels (avoids NaN bilinear contamination).
  float j00 = 1.0, j01 = 0.0, j02 = 0.0;
  float j10 = 0.0, j11 = 1.0, j12 = 0.0;
  float j20 = 0.0, j21 = 0.0, j22 = 1.0;
  if (isValid) {
#if GPUJAC
    j00 = e0.x; j10 = e0.y; j20 = e0.z;   // column 0 = v1-v0
    j01 = e1.x; j11 = e1.y; j21 = e1.z;   // column 1 = v2-v0
    j02 = e2.x; j12 = e2.y; j22 = e2.z;   // column 2 = normal
#else
    int jo = fid * 9;
    j00 = f1(jacTex, jo + 0, twJac); j01 = f1(jacTex, jo + 3, twJac); j02 = f1(jacTex, jo + 6, twJac);
    j10 = f1(jacTex, jo + 1, twJac); j11 = f1(jacTex, jo + 4, twJac); j12 = f1(jacTex, jo + 7, twJac);
    j20 = f1(jacTex, jo + 2, twJac); j21 = f1(jacTex, jo + 5, twJac); j22 = f1(jacTex, jo + 8, twJac);
#endif
  }

  float bx = f1(barimTex, p * 3 + 0, twBarim);
  float by = f1(barimTex, p * 3 + 1, twBarim);
  float bz = f1(barimTex, p * 3 + 2, twBarim);
  int i0 = int(f1(idximTex, p * 3 + 0, twIdxim));
  int i1 = int(f1(idximTex, p * 3 + 1, twIdxim));
  int i2 = int(f1(idximTex, p * 3 + 2, twIdxim));
  vec3 base = bx * vert(i0) + by * vert(i1) + bz * vert(i2);

  float ox = f1(geomTex, 0 * HW + p, twGeom);
  float oy = f1(geomTex, 1 * HW + p, twGeom);
  float oz = f1(geomTex, 2 * HW + p, twGeom);
  float qw = f1(geomTex, 3 * HW + p, twGeom);
  float qx = f1(geomTex, 4 * HW + p, twGeom);
  float qy = f1(geomTex, 5 * HW + p, twGeom);
  float qz = f1(geomTex, 6 * HW + p, twGeom);
  float sx = splatScale(f1(geomTex, 7 * HW + p, twGeom));
  float sy = splatScale(f1(geomTex, 8 * HW + p, twGeom));
  float sz = splatScale(f1(geomTex, 9 * HW + p, twGeom));
  float op = f1(geomTex, 10 * HW + p, twGeom);

  float posx = j00 * ox + j01 * oy + j02 * oz + base.x;
  float posy = j10 * ox + j11 * oy + j12 * oz + base.y;
  float posz = j20 * ox + j21 * oy + j22 * oz + base.z;

  float ql = sqrt(qw * qw + qx * qx + qy * qy + qz * qz);
  if (ql <= 0.0) ql = 1.0;
  float nqw = qw / ql, nqx = qx / ql, nqy = qy / ql, nqz = qz / ql;
  float r00 = 1.0 - 2.0 * (nqy * nqy + nqz * nqz);
  float r01 = 2.0 * (nqx * nqy - nqz * nqw);
  float r02 = 2.0 * (nqx * nqz + nqy * nqw);
  float r10 = 2.0 * (nqx * nqy + nqz * nqw);
  float r11 = 1.0 - 2.0 * (nqx * nqx + nqz * nqz);
  float r12 = 2.0 * (nqy * nqz - nqx * nqw);
  float r20 = 2.0 * (nqx * nqz - nqy * nqw);
  float r21 = 2.0 * (nqy * nqz + nqx * nqw);
  float r22 = 1.0 - 2.0 * (nqx * nqx + nqy * nqy);

  float l00 = r00 * sx, l01 = r01 * sy, l02 = r02 * sz;
  float l10 = r10 * sx, l11 = r11 * sy, l12 = r12 * sz;
  float l20 = r20 * sx, l21 = r21 * sy, l22 = r22 * sz;
  float m00 = j00 * l00 + j01 * l10 + j02 * l20;
  float m01 = j00 * l01 + j01 * l11 + j02 * l21;
  float m02 = j00 * l02 + j01 * l12 + j02 * l22;
  float m10 = j10 * l00 + j11 * l10 + j12 * l20;
  float m11 = j10 * l01 + j11 * l11 + j12 * l21;
  float m12 = j10 * l02 + j11 * l12 + j12 * l22;
  float m20 = j20 * l00 + j21 * l10 + j22 * l20;
  float m21 = j20 * l01 + j21 * l11 + j22 * l21;
  float m22 = j20 * l02 + j21 * l12 + j22 * l22;
  // ── Triangle-bounded clamp (mirrors the Python lift) ──────────────────────
  // The columns of M are exactly world_axis_extents(): M = J.R.diag(exp(s)),
  // and world_axis_extents returns the column norms of J.R times exp(s). So the
  // reach is read straight off M, with no extra work.
  float g2 = 1.0;
  if (uTriKappa > 0.0 && isValid) {
    float triEdge = 0.0, triArea = 0.0;
#if GPUJAC
    // e0 = v1-v0, e1 = v2-v0 are already in scope, and nlen = |e0 x e1|.
    triEdge = max(max(length(e0), length(e1)), length(e1 - e0));
    triArea = 0.5 * length(cross(e0, e1));
#else
    // Columns 0 and 1 of the uploaded Jacobian ARE those two edge vectors.
    vec3 g0 = vec3(j00, j10, j20);
    vec3 g1 = vec3(j01, j11, j21);
    triEdge = max(max(length(g0), length(g1)), length(g1 - g0));
    triArea = 0.5 * length(cross(g0, g1));
#endif
    // quality = sqrt(area) / (0.658 * longest_edge); 1 equilateral, ->0 sliver.
    const float EQ = 0.6580370064762462;   // sqrt(area)/edge for equilateral
    const float DERATE_RATIO = 5.0;        // utils.geom_reg._SLIVER_DERATE_RATIO
    float q = clamp(sqrt(max(triArea, 0.0)) / max(EQ * triEdge, 1e-12), 0.0, 1.0);
    float bound = triEdge;
    if (uSliverQMin > 0.0) {
      bound *= min(1.0, q / (DERATE_RATIO * uSliverQMin));
      if (q < uSliverQMin) op = 0.0;       // sliver hosts no splat
    }
    float reach = max(max(length(vec3(m00, m10, m20)),
                          length(vec3(m01, m11, m21))),
                      length(vec3(m02, m12, m22)));
    float gain = min(1.0, (uTriKappa * bound) / max(reach, 1e-12));
    // Covariance is M.M^T, so scaling M by gain scales it by gain^2 -- six
    // multiplies instead of nine, and the SAME FORM the CPU reference
    // (inference/lift.ts) and the WGSL copy use, so all three read alike.
    g2 = gain * gain;
  }

  float c00 = (m00 * m00 + m01 * m01 + m02 * m02) * g2;
  float c01 = (m00 * m10 + m01 * m11 + m02 * m12) * g2;
  float c02 = (m00 * m20 + m01 * m21 + m02 * m22) * g2;
  float c11 = (m10 * m10 + m11 * m11 + m12 * m12) * g2;
  float c12 = (m10 * m20 + m11 * m21 + m12 * m22) * g2;
  float c22 = (m20 * m20 + m21 * m21 + m22 * m22) * g2;

  o0 = vec4(posx, posy, posz, c00);
  o1 = vec4(c01, c02, c11, c12);
  o2 = vec4(c22, op, 0.0, 0.0);
}`;

// ---------------------------------------------------------------------------
// Eigendecomposition + matToQuat (cyclic Jacobi). Direct port of
// lift_pass2.wgsl eig_decomp_3 / mat_to_quat. `eigDecomp3` writes eigenvalues
// (eigs) and the eigenvector columns (ev0, ev1, ev2). matToQuat → (w,x,y,z).
// Used (P3c) by the spark generator dyno.
// ---------------------------------------------------------------------------
const EIG_GLSL = /* glsl */ `
void eigDecomp3(float a00, float a01, float a02, float a11, float a12, float a22,
                out vec3 eigs, out vec3 ev0, out vec3 ev1, out vec3 ev2) {
  float m00 = a00, m01 = a01, m02 = a02, m11 = a11, m12 = a12, m22 = a22;
  float v00 = 1.0, v01 = 0.0, v02 = 0.0;
  float v10 = 0.0, v11 = 1.0, v12 = 0.0;
  float v20 = 0.0, v21 = 0.0, v22 = 1.0;
  for (int iter = 0; iter < 30; iter++) {
    float diag_mag = abs(m00) + abs(m11) + abs(m22) + 1e-30;
    float o01 = abs(m01), o02 = abs(m02), o12 = abs(m12);
    if (o01 + o02 + o12 < 1e-12 * diag_mag) break;
    int pivot; float app, aqq, apq;
    if (o01 >= o02 && o01 >= o12) { pivot = 0; app = m00; aqq = m11; apq = m01; }
    else if (o02 >= o12)         { pivot = 1; app = m00; aqq = m22; apq = m02; }
    else                          { pivot = 2; app = m11; aqq = m22; apq = m12; }
    if (apq == 0.0) break;
    float theta = (aqq - app) / (2.0 * apq);
    float sgn = (theta >= 0.0) ? 1.0 : -1.0;
    float t = sgn / (abs(theta) + sqrt(1.0 + theta * theta));
    float c = 1.0 / sqrt(1.0 + t * t);
    float s = t * c;
    if (pivot == 0) {
      float n00 = c * c * m00 - 2.0 * s * c * m01 + s * s * m11;
      float n11 = s * s * m00 + 2.0 * s * c * m01 + c * c * m11;
      float n02 = c * m02 - s * m12;
      float n12 = s * m02 + c * m12;
      m00 = n00; m11 = n11; m01 = 0.0; m02 = n02; m12 = n12;
      float a0 = c * v00 - s * v01; float b0 = s * v00 + c * v01;
      float a1 = c * v10 - s * v11; float b1 = s * v10 + c * v11;
      float a2 = c * v20 - s * v21; float b2 = s * v20 + c * v21;
      v00 = a0; v01 = b0; v10 = a1; v11 = b1; v20 = a2; v21 = b2;
    } else if (pivot == 1) {
      float n00 = c * c * m00 - 2.0 * s * c * m02 + s * s * m22;
      float n22 = s * s * m00 + 2.0 * s * c * m02 + c * c * m22;
      float n01 = c * m01 - s * m12;
      float n12 = s * m01 + c * m12;
      m00 = n00; m22 = n22; m02 = 0.0; m01 = n01; m12 = n12;
      float a0 = c * v00 - s * v02; float b0 = s * v00 + c * v02;
      float a1 = c * v10 - s * v12; float b1 = s * v10 + c * v12;
      float a2 = c * v20 - s * v22; float b2 = s * v20 + c * v22;
      v00 = a0; v02 = b0; v10 = a1; v12 = b1; v20 = a2; v22 = b2;
    } else {
      float n11 = c * c * m11 - 2.0 * s * c * m12 + s * s * m22;
      float n22 = s * s * m11 + 2.0 * s * c * m12 + c * c * m22;
      float n01 = c * m01 - s * m02;
      float n02 = s * m01 + c * m02;
      m11 = n11; m22 = n22; m12 = 0.0; m01 = n01; m02 = n02;
      float a0 = c * v01 - s * v02; float b0 = s * v01 + c * v02;
      float a1 = c * v11 - s * v12; float b1 = s * v11 + c * v12;
      float a2 = c * v21 - s * v22; float b2 = s * v21 + c * v22;
      v01 = a0; v02 = b0; v11 = a1; v12 = b1; v21 = a2; v22 = b2;
    }
  }
  eigs = vec3(m00, m11, m22);
  ev0 = vec3(v00, v10, v20);   // column 0
  ev1 = vec3(v01, v11, v21);   // column 1
  ev2 = vec3(v02, v12, v22);   // column 2
}
vec4 matToQuat(float r00, float r01, float r02,
               float r10, float r11, float r12,
               float r20, float r21, float r22) {
  float tr = r00 + r11 + r22;
  float qw, qx, qy, qz;
  if (tr > 0.0) {
    float s = sqrt(tr + 1.0) * 2.0;
    qw = 0.25 * s; qx = (r21 - r12) / s; qy = (r02 - r20) / s; qz = (r10 - r01) / s;
  } else if (r00 > r11 && r00 > r22) {
    float s = sqrt(1.0 + r00 - r11 - r22) * 2.0;
    qw = (r21 - r12) / s; qx = 0.25 * s; qy = (r01 + r10) / s; qz = (r02 + r20) / s;
  } else if (r11 > r22) {
    float s = sqrt(1.0 + r11 - r00 - r22) * 2.0;
    qw = (r02 - r20) / s; qx = (r01 + r10) / s; qy = 0.25 * s; qz = (r12 + r21) / s;
  } else {
    float s = sqrt(1.0 + r22 - r00 - r11) * 2.0;
    qw = (r10 - r01) / s; qx = (r02 + r20) / s; qy = (r12 + r21) / s; qz = 0.25 * s;
  }
  return vec4(qw, qx, qy, qz);
}
bool isFiniteF(float x) { return x == x && abs(x) < 1e30; }`;

// ---------------------------------------------------------------------------
// PASS 2 core — `bool liftSample(...)`: bilinear-sample pass1 at sample s,
// eigendecompose, cull. Returns false when the sample is dropped (validity
// < 0.99 or non-finite). On true, writes pos/scale/quat(w,x,y,z)/color/op.
// Mirrors lift_pass2.wgsl main / lift.ts:236-364. Reused verbatim by the
// spark generator dyno (P3c) — keep it self-contained (samplers as params).
// pass1 textures are RGBA32F of width twP (o0=pos.xyz,c00 / o1=c01,c02,c11,c12
// / o2=c22,op). validTex = HW R32F, colorTex = 3*HW R32F.
// ---------------------------------------------------------------------------
export const LIFT_SAMPLE_GLSL = /* glsl */ `${F1}
${EIG_GLSL}
vec3 p1pos(highp sampler2D o0, int i, int twP) {
  if (i < 0) return vec3(0.0);
  return texelFetch(o0, ivec2(i % twP, i / twP), 0).xyz;
}
void p1cov(highp sampler2D o0, highp sampler2D o1, highp sampler2D o2, int i, int twP, float w,
           inout float a00, inout float a01, inout float a02,
           inout float a11, inout float a12, inout float a22) {
  if (i < 0) return;
  ivec2 uv = ivec2(i % twP, i / twP);
  float c00 = texelFetch(o0, uv, 0).w;
  vec4 m = texelFetch(o1, uv, 0);        // c01,c02,c11,c12
  float c22 = texelFetch(o2, uv, 0).x;
  a00 += w * c00; a01 += w * m.x; a02 += w * m.y;
  a11 += w * m.z; a12 += w * m.w; a22 += w * c22;
}
float p1op(highp sampler2D o2, int i, int twP) {
  if (i < 0) return 0.0;
  return texelFetch(o2, ivec2(i % twP, i / twP), 0).y;
}
vec3 p1col(highp sampler2D colorTex, int i, int twC, int HW) {
  if (i < 0) return vec3(0.0);
  return vec3(f1(colorTex, i, twC), f1(colorTex, HW + i, twC), f1(colorTex, 2 * HW + i, twC));
}
bool liftSample(
    highp sampler2D o0, highp sampler2D o1, highp sampler2D o2,
    highp sampler2D validTex, highp sampler2D colorTex,
    int H, int W, int N, int HW, int twP, int twV, int twC, int s,
    out vec3 pos, out vec3 scale, out vec4 quat, out vec3 color, out float op,
    out ivec4 nbr, out vec4 wgt) {
  pos = vec3(0.0); scale = vec3(0.0); quat = vec4(0.0, 0.0, 0.0, 1.0);
  color = vec3(0.0); op = 0.0; nbr = ivec4(-1); wgt = vec4(0.0);

  int oy = s / N;
  int ox = s - oy * N;
  float invNm1 = 1.0 / float(N - 1);
  float py = float(oy) * invNm1 * float(H) - 0.5;
  float px = float(ox) * invNm1 * float(W) - 0.5;
  int y_lo = int(floor(py)); int y_hi = y_lo + 1; float fy = py - float(y_lo);
  int x_lo = int(floor(px)); int x_hi = x_lo + 1; float fx = px - float(x_lo);

  bool y_lo_in = y_lo >= 0 && y_lo < H;
  bool y_hi_in = y_hi >= 0 && y_hi < H;
  bool x_lo_in = x_lo >= 0 && x_lo < W;
  bool x_hi_in = x_hi >= 0 && x_hi < W;
  int i00 = (y_lo_in && x_lo_in) ? y_lo * W + x_lo : -1;
  int i01 = (y_lo_in && x_hi_in) ? y_lo * W + x_hi : -1;
  int i10 = (y_hi_in && x_lo_in) ? y_hi * W + x_lo : -1;
  int i11 = (y_hi_in && x_hi_in) ? y_hi * W + x_hi : -1;

  float w00 = (1.0 - fx) * (1.0 - fy);
  float w01 = fx * (1.0 - fy);
  float w10 = (1.0 - fx) * fy;
  float w11 = fx * fy;
  nbr = ivec4(i00, i01, i10, i11);   // expose for the SH coeff gather (P4b)
  wgt = vec4(w00, w01, w10, w11);

  float v00 = (i00 >= 0) ? f1(validTex, i00, twV) : 0.0;
  float v01 = (i01 >= 0) ? f1(validTex, i01, twV) : 0.0;
  float v10 = (i10 >= 0) ? f1(validTex, i10, twV) : 0.0;
  float v11 = (i11 >= 0) ? f1(validTex, i11, twV) : 0.0;
  if (w00 * v00 + w01 * v01 + w10 * v10 + w11 * v11 < 0.99) return false;

  vec3 wp = w00 * p1pos(o0, i00, twP) + w01 * p1pos(o0, i01, twP)
          + w10 * p1pos(o0, i10, twP) + w11 * p1pos(o0, i11, twP);

  float a00 = 0.0, a01 = 0.0, a02 = 0.0, a11 = 0.0, a12 = 0.0, a22 = 0.0;
  p1cov(o0, o1, o2, i00, twP, w00, a00, a01, a02, a11, a12, a22);
  p1cov(o0, o1, o2, i01, twP, w01, a00, a01, a02, a11, a12, a22);
  p1cov(o0, o1, o2, i10, twP, w10, a00, a01, a02, a11, a12, a22);
  p1cov(o0, o1, o2, i11, twP, w11, a00, a01, a02, a11, a12, a22);

  vec3 col = w00 * p1col(colorTex, i00, twC, HW) + w01 * p1col(colorTex, i01, twC, HW)
           + w10 * p1col(colorTex, i10, twC, HW) + w11 * p1col(colorTex, i11, twC, HW);
  float opv = w00 * p1op(o2, i00, twP) + w01 * p1op(o2, i01, twP)
            + w10 * p1op(o2, i10, twP) + w11 * p1op(o2, i11, twP);

  vec3 eigs, ev0, ev1, ev2;
  eigDecomp3(a00, a01, a02, a11, a12, a22, eigs, ev0, ev1, ev2);
  if (!isFiniteF(eigs.x) || !isFiniteF(eigs.y) || !isFiniteF(eigs.z) ||
      !isFiniteF(ev0.x) || !isFiniteF(ev0.y) || !isFiniteF(ev0.z) ||
      !isFiniteF(ev1.x) || !isFiniteF(ev1.y) || !isFiniteF(ev1.z) ||
      !isFiniteF(ev2.x) || !isFiniteF(ev2.y) || !isFiniteF(ev2.z) ||
      !isFiniteF(wp.x) || !isFiniteF(wp.y) || !isFiniteF(wp.z)) return false;

  scale = vec3(sqrt(max(1e-12, eigs.x)), sqrt(max(1e-12, eigs.y)), sqrt(max(1e-12, eigs.z)));
  float det = dot(ev0, cross(ev1, ev2));
  float sgn = (det >= 0.0) ? 1.0 : -1.0;
  // M = V (columns = eigenvectors), col 0 sign-flipped; feed ROWS of V to the
  // row-major matToQuat (same coordinate from each column). See lift_pass2.wgsl.
  quat = matToQuat(
    sgn * ev0.x, ev1.x, ev2.x,
    sgn * ev0.y, ev1.y, ev2.y,
    sgn * ev0.z, ev1.z, ev2.z);
  pos = wp;
  color = clamp(col, 0.0, 1.0);
  op = opv;
  return true;
}`;

// ---------------------------------------------------------------------------
// P4b — view-dependent SH color evaluated IN the generator dyno, from the
// untile's KC-channel coeff buffer (channel-major (k*3+c)*HW+p). Reuses the
// bilinear neighbours/weights `liftSample` already computed (nbr/wgt), so the
// coeffs are sampled at the EXACT same position pass2 used — interp-then-eval ==
// eval-then-interp (lift_coeffs.wgsl). The basis is the 1:1 port from
// `shModifier.ts` / `wgsl/sh_color.wgsl`; its frontal-cone guard is NOT ported —
// the server fits over the whole sphere, so there is no domain to attenuate
// outside. Requires F1 (from LIFT_SAMPLE_GLSL) to be included first.
// ---------------------------------------------------------------------------
export const SH_EVAL_BILINEAR_GLSL = /* glsl */ `
vec3 shCoeffBand(highp sampler2D colorTex, int twC, int HW, int kc, ivec4 nbr, vec4 wgt) {
  vec3 r = vec3(0.0);
  if (nbr.x >= 0) r += wgt.x * vec3(f1(colorTex,(kc+0)*HW+nbr.x,twC), f1(colorTex,(kc+1)*HW+nbr.x,twC), f1(colorTex,(kc+2)*HW+nbr.x,twC));
  if (nbr.y >= 0) r += wgt.y * vec3(f1(colorTex,(kc+0)*HW+nbr.y,twC), f1(colorTex,(kc+1)*HW+nbr.y,twC), f1(colorTex,(kc+2)*HW+nbr.y,twC));
  if (nbr.z >= 0) r += wgt.z * vec3(f1(colorTex,(kc+0)*HW+nbr.z,twC), f1(colorTex,(kc+1)*HW+nbr.z,twC), f1(colorTex,(kc+2)*HW+nbr.z,twC));
  if (nbr.w >= 0) r += wgt.w * vec3(f1(colorTex,(kc+0)*HW+nbr.w,twC), f1(colorTex,(kc+1)*HW+nbr.w,twC), f1(colorTex,(kc+2)*HW+nbr.w,twC));
  return r;
}
vec3 shEvalBilinear(highp sampler2D colorTex, int twC, int HW, ivec4 nbr, vec4 wgt,
                    vec3 center, vec3 camPos, int K) {
  vec3 d = normalize(camPos - center);
  float x = d.x, y = d.y, z = d.z;
  float Y[16];
  Y[0] = 0.2820947918;
  Y[1] = 0.4886025119 * y;  Y[2] = 0.4886025119 * z;  Y[3] = 0.4886025119 * x;
  Y[4] = 1.0925484306 * x * y;          Y[5] = 1.0925484306 * y * z;
  Y[6] = 0.3153915652 * (3.0*z*z - 1.0); Y[7] = 1.0925484306 * x * z;
  Y[8] = 0.5462742153 * (x*x - y*y);
  if (K > 9) {
    Y[9]  = 0.5900435899 * y * (3.0*x*x - y*y);
    Y[10] = 2.8906114426 * x * y * z;
    Y[11] = 0.4570457995 * y * (5.0*z*z - 1.0);
    Y[12] = 0.3731763326 * z * (5.0*z*z - 3.0);
    Y[13] = 0.4570457995 * x * (5.0*z*z - 1.0);
    Y[14] = 1.4453057213 * z * (x*x - y*y);
    Y[15] = 0.5900435899 * x * (x*x - 3.0*y*y);
  }
  vec3 col = Y[0] * shCoeffBand(colorTex, twC, HW, 0, nbr, wgt);
  for (int k = 1; k < K; k++) col += Y[k] * shCoeffBand(colorTex, twC, HW, k*3, nbr, wgt);
  // No domain guard: the server fits over the whole sphere, so the coefficients are
  // valid from every direction and there is nowhere to attenuate.
  return clamp(col, 0.0, 1.0);
}`;

// Trivial fullscreen-triangle vertex shader for all GPGPU passes.
export const FULLSCREEN_VS = /* glsl */ `#version 300 es
precision highp float;
void main() {
  vec2 p = vec2((gl_VertexID << 1) & 2, gl_VertexID & 2);
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;
